/** Tiny DOM helpers shared by the UI modules. */

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', html = ''): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html) e.innerHTML = html;
  return e;
}

export const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Wait for `ms` or until `signal` resolves (whichever first). */
export function waitOr(ms: number, signal?: Promise<unknown>): Promise<void> {
  return signal ? Promise.race([wait(ms), signal.then(() => undefined)]) : wait(ms);
}

export function fmtTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}

/** Wrap each character in a span for staggered letter animations (words never break). */
export function letters(text: string, cls = 'ltr'): string {
  let i = 0;
  return text
    .split(' ')
    .map((word) => `<span class="w">${[...word].map((ch) => `<span class="${cls}" style="--i:${i++}">${escapeHtml(ch)}</span>`).join('')}</span>`)
    .join('<span class="sp"> </span>');
}

/** Force a reflow so a subsequent class change animates. */
export function reflow(e: HTMLElement): void {
  void e.offsetWidth;
}

/** Next animation frame as a promise. */
export const nextFrame = (): Promise<number> => new Promise((r) => requestAnimationFrame(r));

/** Format a hex colour with alpha (e.g. rgba(...)). */
export function withAlpha(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

export const prefersReducedMotion = (): boolean =>
  typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
